import { Camera } from '../render/camera.ts';
import { GameEngine } from '../simulation/gameEngine.ts';
import { Haptics } from '../utils/haptics.ts';

interface PointerRecord {
  startX: number;
  startY: number;
  startTime: number;
  currentX: number;
  currentY: number;
}

export class PointerController {
  private canvas: HTMLCanvasElement;
  private camera: Camera;
  private engine: GameEngine;

  private activePointers = new Map<number, PointerRecord>();
  private isDragging = false;
  private isPinching = false;
  private primaryPointerId: number | null = null;
  private lastPointerX = 0;
  private lastPointerY = 0;

  // 2-finger pinch & pan state
  private initialPinchDist = 0;
  private lastPinchMidX = 0;
  private lastPinchMidY = 0;

  // Camera follow state
  private followPlayer = true;

  constructor(canvas: HTMLCanvasElement, camera: Camera, engine: GameEngine) {
    this.canvas = canvas;
    this.camera = camera;
    this.engine = engine;

    this.setupListeners();
  }

  private setupListeners(): void {
    this.canvas.addEventListener('pointerdown', this.handlePointerDown);
    this.canvas.addEventListener('wheel', this.handleWheel, { passive: false });
    window.addEventListener('pointermove', this.handlePointerMove);
    window.addEventListener('pointerup', this.handlePointerUp);
    window.addEventListener('pointercancel', this.handlePointerUp);

    // Keyboard support: Space to kick QTE
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space') {
        const state = this.engine.getState();
        if (state.qteKick.active) {
          e.preventDefault();
          this.engine.executeQTEKick();
        }
      }
    });
  }

  public destroy(): void {
    this.canvas.removeEventListener('pointerdown', this.handlePointerDown);
    this.canvas.removeEventListener('wheel', this.handleWheel);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerup', this.handlePointerUp);
    window.removeEventListener('pointercancel', this.handlePointerUp);
  }

  private handleWheel = (e: WheelEvent): void => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 0.87;
    const rect = this.canvas.getBoundingClientRect();
    this.camera.zoomBy(factor, e.clientX - rect.left, e.clientY - rect.top);
    this.followPlayer = false; // Ngừng follow khi người chơi chủ động zoom bằng chuột
  };

  private handlePointerDown = (e: PointerEvent): void => {
    try {
      this.canvas.setPointerCapture?.(e.pointerId);
    } catch {
      // Ignore if not supported
    }

    const record: PointerRecord = {
      startX: e.clientX,
      startY: e.clientY,
      startTime: performance.now(),
      currentX: e.clientX,
      currentY: e.clientY,
    };
    this.activePointers.set(e.pointerId, record);

    if (this.activePointers.size === 1) {
      this.primaryPointerId = e.pointerId;
      this.lastPointerX = e.clientX;
      this.lastPointerY = e.clientY;
      this.isDragging = false;
      this.isPinching = false;
    } else if (this.activePointers.size === 2) {
      // Bắt đầu 2 ngón tay: Pinch-to-zoom & Pan
      this.isPinching = true;
      this.isDragging = false;
      this.followPlayer = false;

      const pts = Array.from(this.activePointers.values());
      this.initialPinchDist = Math.hypot(
        pts[0]!.currentX - pts[1]!.currentX,
        pts[0]!.currentY - pts[1]!.currentY
      );
      this.lastPinchMidX = (pts[0]!.currentX + pts[1]!.currentX) / 2;
      this.lastPinchMidY = (pts[0]!.currentY + pts[1]!.currentY) / 2;
    }
  };

  private handlePointerMove = (e: PointerEvent): void => {
    const record = this.activePointers.get(e.pointerId);
    if (!record) return;

    record.currentX = e.clientX;
    record.currentY = e.clientY;

    // 1. Pinch-to-zoom & Pan bản đồ bằng 2 ngón tay (vừa phóng to vừa trượt bản đồ theo tay)
    if (this.activePointers.size === 2) {
      const pts = Array.from(this.activePointers.values());
      const currentDist = Math.hypot(
        pts[0]!.currentX - pts[1]!.currentX,
        pts[0]!.currentY - pts[1]!.currentY
      );
      const currentMidX = (pts[0]!.currentX + pts[1]!.currentX) / 2;
      const currentMidY = (pts[0]!.currentY + pts[1]!.currentY) / 2;

      // Độ dịch chuyển trung điểm (Pan delta khi ngón tay trượt)
      const deltaX = currentMidX - this.lastPinchMidX;
      const deltaY = currentMidY - this.lastPinchMidY;
      if (Math.hypot(deltaX, deltaY) > 0.5) {
        this.camera.pan(deltaX, deltaY);
        this.lastPinchMidX = currentMidX;
        this.lastPinchMidY = currentMidY;
        this.followPlayer = false;
      }

      // Độ co dãn khoảng cách (Zoom scale)
      if (this.initialPinchDist > 5 && currentDist > 5) {
        const factor = currentDist / this.initialPinchDist;
        const rect = this.canvas.getBoundingClientRect();
        const midScreenX = currentMidX - rect.left;
        const midScreenY = currentMidY - rect.top;
        this.camera.zoomBy(factor, midScreenX, midScreenY);
        this.initialPinchDist = currentDist;
        this.followPlayer = false;
      }
      return;
    }

    // 2. Kéo rê 1 ngón tay: PAN CUỘN BẢN ĐỒ (Smooth 1-finger Pan)
    if (this.activePointers.size === 1 && e.pointerId === this.primaryPointerId) {
      const moveFromStart = Math.hypot(
        e.clientX - record.startX,
        e.clientY - record.startY
      );

      // Ngưỡng phân biệt Tap và Pan (>= 6px coi như người dùng muốn trượt bản đồ)
      if (this.isDragging || moveFromStart > 6) {
        this.isDragging = true;
        this.followPlayer = false; // Tạm dừng follow để người chơi tự do soi ngõ ngách

        const deltaX = e.clientX - this.lastPointerX;
        const deltaY = e.clientY - this.lastPointerY;
        this.lastPointerX = e.clientX;
        this.lastPointerY = e.clientY;

        this.camera.pan(deltaX, deltaY);
      }
    }
  };

  private handlePointerUp = (e: PointerEvent): void => {
    const record = this.activePointers.get(e.pointerId);

    // Xử lý 1-Touch Tap (Chạm nhả nhanh không kéo rê: Đặt địa điểm hướng tới cho chó)
    if (
      record &&
      this.activePointers.size === 1 &&
      e.pointerId === this.primaryPointerId &&
      !this.isDragging
    ) {
      const elapsed = performance.now() - record.startTime;
      const dist = Math.hypot(
        e.clientX - record.startX,
        e.clientY - record.startY
      );

      // Nếu chạm nhanh dưới 400ms và dịch chuyển dưới 10px -> 1-TOUCH CHỌN ĐÍCH ĐẾN!
      if (elapsed < 400 && dist < 10) {
        this.directLeadToScreenPos(e.clientX, e.clientY);
        this.followPlayer = true; // Khi ra lệnh bước đi, camera tự động follow theo
        Haptics.tap();
      }
    }

    this.activePointers.delete(e.pointerId);

    try {
      this.canvas.releasePointerCapture?.(e.pointerId);
    } catch {
      // Ignore if not captured
    }

    if (this.activePointers.size === 0) {
      this.isDragging = false;
      this.isPinching = false;
      this.primaryPointerId = null;
    } else if (this.activePointers.size === 1) {
      // Còn lại 1 ngón sau khi nhả ngón kia
      const remainingEntry = Array.from(this.activePointers.entries())[0]!;
      this.primaryPointerId = remainingEntry[0];
      this.lastPointerX = remainingEntry[1].currentX;
      this.lastPointerY = remainingEntry[1].currentY;
      // Đánh dấu isDragging để tránh kích hoạt tap khi nhấc nốt ngón còn lại
      this.isDragging = true;
    }
  };

  /**
   * Chuyển đổi vị trí chạm 1-touch trên màn hình thành tọa độ thế giới thực và dẫn nhân vật đi tới đó
   */
  private directLeadToScreenPos(clientX: number, clientY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const screenX = clientX - rect.left;
    const screenY = clientY - rect.top;

    const worldPoint = this.camera.screenToWorld(screenX, screenY);
    this.engine.setPlayerTarget(worldPoint);
  }

  public enableFollow(): void {
    this.followPlayer = true;
  }

  public disableFollow(): void {
    this.followPlayer = false;
  }

  public shouldFollowPlayer(): boolean {
    return this.followPlayer && this.activePointers.size === 0;
  }

  public isInteracting(): boolean {
    return this.activePointers.size > 0;
  }
}
